import type { GetPrescriptionsForPatientParams } from '@lotushealth/lotushealth_js_sdk/schemas';
import type {
  GetMedicationMonographParams,
  GetMyNotificationCountsParams,
  GetMyTransmissionErrorsParams,
  GetPendingRxChangesDetailedParams,
  GetPendingRefillsDetailedParams,
  GetPendingRefillsParams,
  SearchAllergensParams,
  SearchMedicationsParams,
  SearchSuppliesParams,
  SelectMedicationParams,
} from '@lotushealth/lotushealth_js_sdk/schemas';
import type { SearchPharmaciesInput } from '../api/searchPharmacies.js';

/**
 * Query keys for DoseSpot Rx-related data.
 */
export const rxKeys = {
  all: ['rx'] as const,

  dosespotPatientInfo: (userId: string) => ['rx', 'patient', userId, 'info'] as const,
  dosespotPatientExchange: (dosespotPatientId: string) =>
    ['rx', 'patient', dosespotPatientId, 'exchange'] as const,

  patientAllergies: (userId: string) => ['rx', 'patient', userId, 'allergies'] as const,
  patientPharmacies: (userId: string) => ['rx', 'patient', userId, 'pharmacies'] as const,

  patientPrescriptions: (userId: string, params?: GetPrescriptionsForPatientParams) =>
    ['rx', 'patient', userId, 'prescriptions', ...(params ? [params] : [])] as const,

  prescription: (userId: string, dosespotPrescriptionId: string) =>
    ['rx', 'patient', userId, 'prescription', dosespotPrescriptionId] as const,
  prescriptionLogs: (userId: string, dosespotPrescriptionId: string) =>
    ['rx', 'patient', userId, 'prescription', dosespotPrescriptionId, 'logs'] as const,

  diagnosesSearch: (searchTerm: string) => ['rx', 'diagnoses', 'search', searchTerm] as const,

  medicationsSearch: (params: SearchMedicationsParams) =>
    ['rx', 'medications', 'search', params] as const,
  medicationsSearchByRxcui: (rxcui: string) =>
    ['rx', 'medications', 'search-by-rxcui', rxcui] as const,
  medicationSelect: (params: SelectMedicationParams) =>
    ['rx', 'medications', 'select', params] as const,
  medicationMonograph: (params: GetMedicationMonographParams) =>
    ['rx', 'medications', 'monograph', params] as const,

  pharmaciesSearch: (input: SearchPharmaciesInput) => ['rx', 'pharmacy', 'search', input] as const,
  pharmacyById: (pharmacyId: string) => ['rx', 'pharmacy', pharmacyId] as const,

  suppliesSearch: (params: SearchSuppliesParams) => ['rx', 'supplies', 'search', params] as const,
  supplyById: (supplyId: number) => ['rx', 'supplies', supplyId] as const,

  allergensSearch: (params: SearchAllergensParams) =>
    ['rx', 'allergens', 'search', params] as const,
  allergenById: (allergenId: string) => ['rx', 'allergens', allergenId] as const,

  myNotificationCounts: (params?: GetMyNotificationCountsParams) =>
    ['rx', 'notifications', 'counts', ...(params ? [params] : [])] as const,
  myTransmissionErrors: (params?: GetMyTransmissionErrorsParams) =>
    ['rx', 'notifications', 'errors', ...(params ? [params] : [])] as const,
  patientByDosespotId: (dosespotPatientId: string) =>
    ['rx', 'notifications', 'patient', dosespotPatientId] as const,
  prescriptionByDosespotId: (patientId: string, dosespotPrescriptionId: string) =>
    ['rx', 'notifications', 'prescription', patientId, dosespotPrescriptionId] as const,

  pendingRefills: (params?: GetPendingRefillsParams) =>
    ['rx', 'refills', 'pending', ...(params ? [params] : [])] as const,
  pendingRefillsDetailed: (params?: GetPendingRefillsDetailedParams) =>
    ['rx', 'refills', 'pending-detailed', ...(params ? [params] : [])] as const,

  pendingRxChangesDetailed: (params?: GetPendingRxChangesDetailedParams) =>
    ['rx', 'rxchanges', 'pending-detailed', ...(params ? [params] : [])] as const,

  dispenseUnits: () => ['rx', 'dispense-units'] as const,

  previewPriorAuth: (userId: string, params: PreviewPriorAuthQueryKeyParams) =>
    ['rx', 'patient', userId, 'preview-prior-auth', params] as const,

  patientFormulary: (userId: string, params: PatientFormularyQueryKeyParams) =>
    ['rx', 'patient', userId, 'formulary', params] as const,
};

/**
 * The narrow, plain-object projection of a prescription draft that feeds
 * the prior-auth preview. Included verbatim in the query key so
 * react-query automatically re-fetches when any of the inputs that
 * affect PA posture change — while ignoring the rest of the draft
 * (directions, notes, etc.) which don't.
 */
export interface PreviewPriorAuthQueryKeyParams {
  dispensableDrugId: number | null;
  pharmacyId: number | null;
  quantity: number | null;
  daysSupply: number | null;
  dispenseUnitTypeId: number | null;
  patientEligibilityId: number | null;
}

/**
 * Inputs that scope a formulary lookup. ``dispensableDrugId`` xor
 * ``ndc`` is required (we'll resolve the NDC server-side from the
 * dispensable drug id if needed). ``pharmacyId`` and
 * ``patientEligibilityId`` are optional — the backend defaults to the
 * patient's primary Retail coverage when they're absent, which is the
 * correct behaviour for the medication-selection step before a pharmacy
 * has been chosen.
 */
export interface PatientFormularyQueryKeyParams {
  dispensableDrugId: number | null;
  ndc: string | null;
  pharmacyId: number | null;
  patientEligibilityId: number | null;
}
